import * as React from 'react';

/** How long the pill holds after a refused click before fading back out. */
export const BLOCKED_NOTICE_VISIBLE_MS = 2000;

export interface IAlertBlockedNotice {
  /** The row currently showing the "available when assistant finishes" pill, or `null`. */
  blockedNoticeAlertId: string | null;
  /** Puts the pill on a row and restarts the auto-dismiss clock. */
  showBlockedNotice: (alertId: string) => void;
}

/**
 * The transient refusal feedback for an alert clicked mid-turn: at most one
 * row shows the pill, a re-click moves it (or restarts its clock on the same
 * row), and it clears after {@link BLOCKED_NOTICE_VISIBLE_MS}.
 *
 * The pill promises the row unlocks when the assistant finishes, so it must
 * not outlive the turn — when `isTurnInFlight` drops, the notice clears
 * immediately instead of letting the timer contradict a row that is already
 * clickable.
 *
 * @returns `blockedNoticeAlertId` — the row currently showing the pill, or
 *   `null` — and `showBlockedNotice(alertId)`, which puts the pill on a row
 *   and restarts its auto-dismiss clock. Both stable except for the id itself.
 */
export function useAlertBlockedNotice(isTurnInFlight: boolean): IAlertBlockedNotice {
  const [blockedNoticeAlertId, setBlockedNoticeAlertId] = React.useState<string | null>(null);
  const timerRef = React.useRef<number | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showBlockedNotice = React.useCallback(
    (alertId: string) => {
      setBlockedNoticeAlertId(alertId);
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setBlockedNoticeAlertId(null);
      }, BLOCKED_NOTICE_VISIBLE_MS);
    },
    [clearTimer]
  );

  // Layout-synchronous for the same reason the pane publishes the flag that
  // way: the clear must land in the same paint as the turn ending, not a
  // frame after it.
  React.useLayoutEffect(() => {
    if (isTurnInFlight) {
      return;
    }
    clearTimer();
    setBlockedNoticeAlertId(null);
  }, [isTurnInFlight, clearTimer]);

  React.useEffect(() => clearTimer, [clearTimer]);

  return { blockedNoticeAlertId, showBlockedNotice };
}
